import { type FC } from 'react';
import { COMPANY, TESTIMONIALS } from '../content';
import { Container, TitleBlock } from '../primitives';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .replace(/[^A-Za-z]/g, '')
    .slice(0, 2)
    .toUpperCase();

const QuoteMark: FC = () => (
  <svg width="28" height="22" viewBox="0 0 28 22" fill="currentColor" aria-hidden="true">
    <path d="M0 22V13.2C0 5.9 3.9 1.5 11.2 0l1.3 2.9C8.6 4.3 6.7 7 6.5 10.5H11.9V22H0Zm16.1 0V13.2c0-7.3 3.9-11.7 11.2-13.2L28 2.9c-3.9 1.4-5.8 4.1-6 7.6h5.4V22H16.1Z" />
  </svg>
);

const ExternalIcon: FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M6 3H3v10h10v-3M9 2h5v5M14 2 7.5 8.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Testimonials: FC = () => (
  <section className="py-20 sm:py-28">
    <Container>
      <TitleBlock sheet="DB-03 - Word on site" title="What clients say when the job's done" />

      <div className="grid gap-5 md:grid-cols-3">
        {TESTIMONIALS.map((testimonial) => (
          <figure
            key={testimonial.name}
            className="flex flex-col rounded-lg border border-ink-200 bg-white p-6 transition-shadow duration-200 hover:shadow-lg sm:p-7"
          >
            <span className="text-brand-500">
              <QuoteMark />
            </span>
            <blockquote className="mt-5 flex-1 text-[15px] leading-relaxed text-ink-800">
              {testimonial.quote}
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-3 border-t border-ink-100 pt-5">
              <span
                aria-hidden="true"
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-ink-900 font-display text-sm font-bold text-white"
              >
                {initials(testimonial.name)}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-semibold text-ink-900">{testimonial.name}</span>
                <span className="font-mono text-[11px] tracking-wide text-ink-500 uppercase">
                  {testimonial.job} · {testimonial.location}
                </span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-6 flex flex-col items-start gap-5 rounded-lg border border-dashed border-ink-300 bg-white/60 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
        <div>
          <p className="font-display text-lg font-bold text-ink-900">Had us on your job?</p>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-ink-600">
            A quick Google review helps other Waikato homeowners and builders find a crew they
            can trust. It takes about a minute.
          </p>
        </div>
        <a
          href={COMPANY.googleReviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-conversion="google-review-click"
          className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-md bg-ink-900 px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
        >
          Leave a Google review
          <ExternalIcon />
        </a>
      </div>
    </Container>
  </section>
);
